import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageGallery from 'react-image-gallery';

import { RTL } from 'arbitrip-common/translation/const';

import roomPlaceholder from '@/img/hotel_page/room_placeholder.png';

import { IconChevron, IconPhotos } from './Rm3Icons';

const STEP_PREV = -1;
const STEP_NEXT = 1;

/**
 * Container photos (spec §1.2): prev/next wrap around with the room-details modal's slide (`react-image-gallery`),
 * count badge at inline-end, click opens room details. The modal reuses it (spec P2.5) through the optional props;
 * their defaults keep the page as it was.
 */
function Rm3PhotoCarousel({ images, alt, onOpen, startIndex = 0, onSlide, galleryRef, className, showCount = true }) {
    const { t, i18n } = useTranslation();
    const [failedUrls, setFailedUrls] = useState(() => new Set());
    const ownGalleryRef = useRef(null);
    const gallery = galleryRef || ownGalleryRef;
    const isRtl = i18n.dir() === RTL;
    const count = images.length;
    const items = useMemo(() => images.map((url) => ({ original: url })), [images]);
    // Remount on a different photo list so the gallery's index never points past the new list.
    const galleryKey = useMemo(() => images.join(' '), [images]);
    const imageClassName = `rm3-photo__image${onOpen ? ' rm3-photo__image--clickable' : ''}`;

    const markFailed = (url) => {
        setFailedUrls((previous) => (previous.has(url) ? previous : new Set(previous).add(url)));
    };

    const renderItem = (item) => (
        <img
            className={imageClassName}
            src={failedUrls.has(item.original) ? roomPlaceholder : item.original}
            alt={alt}
            onClick={onOpen || undefined}
            onError={() => markFailed(item.original)}
            data-testid="rm3-room-photo"
        />
    );

    // The gallery reports a slide only after its transition; the owner hears the target at once (a skipped click is
    // corrected by that later report).
    const reportSlide = (step) => {
        if (onSlide && gallery.current && count > 0) {
            onSlide((gallery.current.getCurrentIndex() + step + count) % count);
        }
    };

    const renderNav = (direction) =>
        function Rm3PhotoNav(slide, disabled) {
            return (
                <button
                    type="button"
                    className={`rm3-photo__nav rm3-photo__nav--${direction}`}
                    aria-label={t(direction === 'prev' ? 'HotelDealsRm3.PreviousImage' : 'HotelDealsRm3.NextImage')}
                    disabled={disabled}
                    onClick={(event) => {
                        event.stopPropagation();
                        reportSlide(direction === 'prev' ? STEP_PREV : STEP_NEXT);
                        slide();
                    }}
                    data-testid={`rm3-photo-${direction}`}
                >
                    <IconChevron className={`rm3-chevron${direction === 'prev' ? ' rm3-chevron--back' : ''}`} />
                </button>
            );
        };

    return (
        <div className={className ? `rm3-photo ${className}` : 'rm3-photo'}>
            {count > 0 ? (
                <ImageGallery
                    key={galleryKey}
                    ref={gallery}
                    items={items}
                    startIndex={Math.min(Math.max(startIndex, 0), count - 1)}
                    onSlide={onSlide}
                    renderItem={renderItem}
                    // The gallery's left/right callbacks are physical; with isRTL, "left" slides to the next image.
                    renderLeftNav={renderNav(isRtl ? 'next' : 'prev')}
                    renderRightNav={renderNav(isRtl ? 'prev' : 'next')}
                    isRTL={isRtl}
                    infinite
                    lazyLoad
                    disableArrowKeys
                    showThumbnails={false}
                    showPlayButton={false}
                    showFullscreenButton={false}
                />
            ) : (
                <img
                    className={imageClassName}
                    src={roomPlaceholder}
                    alt={alt}
                    onClick={onOpen || undefined}
                    data-testid="rm3-room-photo"
                />
            )}
            {showCount && count > 0 && (
                <div
                    className="rm3-photo__count"
                    aria-label={t('HotelDealsRm3.PhotoCount', { count })}
                    data-testid="rm3-photo-count"
                >
                    <IconPhotos />
                    <span dir="ltr">{count}</span>
                </div>
            )}
        </div>
    );
}

export default Rm3PhotoCarousel;
